/**
 * Button.
 *
 * Implements the Figma component set `🛠️ Components / Button` (node `494:2861`) in
 * full: two types, an optional trailing icon, and all six states.
 *
 * | State | Primary | Secondary |
 * |---|---|---|
 * | Default | `blue-dark-200` fill | `blue-dark-400` border and label |
 * | Hover | `blue-dark-300` fill | `blue-dark-300` border and label |
 * | Active | `blue-dark-400` fill | white fill, `blue-dark-500` border and label |
 * | Focused | `blue-dark-400` fill + 6px `grey-600` ring | `grey-600` fill, `blue-dark-400` border |
 * | Disabled | 20% opacity | 20% opacity |
 *
 * Geometry: 45px tall, 32px horizontal padding, `Radius/m`, `Desktop/Text/body`.
 *
 * Three deviations from the file, all deliberate:
 *
 * 1. **Focus is a ring, not a border.** Figma draws the focused state as a 6px border,
 *    which would resize the button on focus. A ring is a box-shadow, so nothing moves.
 * 2. **`Button_icon / primary / Hover` has a grey label (`#545454`) in Figma** while the
 *    text-only hover has a white one. That reads as a mistake in the file, so both use
 *    white here. Worth asking the designer.
 * 3. **The file's `Visited` state is not wired to `:visited`.** It was, and it was wrong:
 *    every call to action pointing at a page the visitor had already opened rendered
 *    `blue-dark-500` (#000f76) instead of the design's `#0948f8`. On a site where the
 *    menu links to fourteen pages, that is most buttons after five minutes of browsing -
 *    the brand colour quietly turning navy, and the reason it was reported as "the blue
 *    on the site is darker than in Figma". Proved by putting the real button beside a
 *    clone pointing at an address the browser had never seen: same markup, two colours.
 *
 *    A `Visited` state makes sense for a text link, which is what browsers have always
 *    styled that way. A filled button is a control, not a citation, and its fill is the
 *    brand's. If the designer wants it back it belongs on `CtaLink`'s link variant, not
 *    here.
 *
 * **The focus ring is `grey-800`, not the file's `grey-600`, and the secondary variant
 * has one at all.** `grey-600` (#cdcdcd) is 1.59:1 against a white page - a 6px halo
 * nobody can see, and under the 3:1 that WCAG 1.4.11 asks of a focus indicator. One step
 * darker is 4.17:1 on the page and 3.02:1 against the focused fill, so the ring works
 * from both sides while staying the soft grey the design intends.
 *
 * The secondary variant's focused state was a fill change alone - white to #cdcdcd,
 * which is the same 1.59:1 nobody can see - so it takes the same ring. Both are
 * deviations from the component set and both are the designer's to bless.
 */
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { AppLink } from './AppLink';
import { Icon } from './Icon';

export type ButtonVariant = 'primary' | 'secondary';

const BASE = 'transition-colors focus-visible:outline-none disabled:pointer-events-none';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: [
    'bg-blue-dark-200 text-white',
    'hover:bg-blue-dark-300',
    'active:bg-blue-dark-400',
    'focus-visible:bg-blue-dark-400 focus-visible:ring-[6px] focus-visible:ring-grey-800',
    'disabled:bg-blue-dark-400 disabled:opacity-20 aria-disabled:bg-blue-dark-400 aria-disabled:opacity-20'
  ].join(' '),
  secondary: [
    'border border-blue-dark-400 text-blue-dark-400',
    'hover:border-blue-dark-300 hover:text-blue-dark-300',
    'active:border-blue-dark-500 active:bg-white active:text-blue-dark-500',
    'focus-visible:border-blue-dark-400 focus-visible:bg-grey-600 focus-visible:text-blue-dark-400',
    'focus-visible:ring-[6px] focus-visible:ring-grey-800',
    'disabled:opacity-20 aria-disabled:opacity-20'
  ].join(' ')
};

/**
 * The same six states as flat classes, so the gallery can show them side by side the
 * way Figma does - a pseudo-class cannot be forced from JavaScript.
 *
 * Keep this in step with VARIANTS above. It is written out literally rather than built
 * by prefixing, because Tailwind's scanner only sees complete class names in the source.
 */
export const BUTTON_STATE_PREVIEW: Record<ButtonVariant, Record<string, string>> = {
  primary: {
    Default: 'bg-blue-dark-200 text-white',
    Hover: 'bg-blue-dark-300 text-white',
    Active: 'bg-blue-dark-400 text-white',
    Focused: 'bg-blue-dark-400 text-white ring-[6px] ring-grey-800',
    Disabled: 'bg-blue-dark-400 text-white opacity-20'
  },
  secondary: {
    Default: 'border border-blue-dark-400 text-blue-dark-400',
    Hover: 'border border-blue-dark-300 text-blue-dark-300',
    Active: 'border border-blue-dark-500 bg-white text-blue-dark-500',
    Focused: 'border border-blue-dark-400 bg-grey-600 text-blue-dark-400 ring-[6px] ring-grey-800',
    Disabled: 'border border-blue-dark-400 text-blue-dark-400 opacity-20'
  }
};

/** Geometry shared by the real button and the previews. */
export const BUTTON_SHAPE = 'inline-flex h-[45px] w-fit items-center justify-center gap-1.5 rounded-m px-8 text-body';

interface CommonProps {
  variant?: ButtonVariant;
  /** The trailing arrow - Figma's `Category=Button_icon`. */
  withIcon?: boolean;
  children: ReactNode;
  className?: string;
}

type ButtonAsButton = CommonProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & { href?: undefined; to?: undefined };
type ButtonAsLink = CommonProps & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'> & { href: string; to?: undefined };
/**
 * `to` is a URL an editor wrote: it gets the language prefix and routes client-side.
 * `href` stays the escape hatch for a URL the caller has already resolved.
 */
type ButtonAsAppLink = CommonProps & { to: string; href?: undefined; 'aria-label'?: string };

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAppLink;

export function Button(props: ButtonProps) {
  const { variant = 'primary', withIcon = false, children, className, ...rest } = props;
  const classes = `${BUTTON_SHAPE} ${BASE} ${VARIANTS[variant]} ${className ?? ''}`;
  const content = (
    <>
      {children}
      {withIcon ? <Icon name="arrow-right" size={18} /> : null}
    </>
  );

  if ('to' in rest && rest.to !== undefined) {
    const { to, ...anchorProps } = rest as { to: string };
    return <AppLink to={to} {...anchorProps} className={classes}>{content}</AppLink>;
  }
  if ('href' in rest && rest.href !== undefined) {
    return <a {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)} className={classes}>{content}</a>;
  }
  return <button type="button" {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)} className={classes}>{content}</button>;
}
